import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientSwitcher from "@/components/ClientSwitcher";
import CheckGroup from "@/components/CheckGroup";
import SignaturePad from "@/components/SignaturePad";
import MonthNav from "@/components/MonthNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Circle, Pill, Plus, Trash2, Info, Target, AlertTriangle, Sparkles, Loader2, TrendingUp, Minus, ShieldAlert, Droplets, Clock, Flag } from "lucide-react";
import { toast } from "sonner";
import { StatusButton } from "@/components/daily-note/StatusButton";
import {
  pad,
  todayISO,
  STATUSES,
  COMPLIANCE_ITEMS,
  GOAL_STATUSES,
  splitSupportLines,
  medUsesSlot,
} from "@/lib/dailyNote";

export default function DailyNoteForm() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [config, setConfig] = useState({ sections: [], custom_questions: [] });
  const [goals, setGoals] = useState([]);
  const [allMeds, setAllMeds] = useState([]);
  const [cal, setCal] = useState(null);
  const [calY, setCalY] = useState(new Date().getFullYear());
  const [calM, setCalM] = useState(new Date().getMonth() + 1);

  const [form, setForm] = useState({
    date: search.get("date") || todayISO(),
    attendance: "present",
    overnight_stayed: false,
    overnight_reason: "",
    activities: [],
    mood: [],
    meals: [],
    attendance_checks: [],
    summary_note: "",
    signature_type: "typed",
    signature_data: "",
  });
  const [goalProgress, setGoalProgress] = useState({}); // goalId -> {worked_on, progress, status}
  const [customAnswers, setCustomAnswers] = useState({});
  const [emarEntries, setEmarEntries] = useState({}); // medId -> {am, pm, missed_reason}
  const [prnEntries, setPrnEntries] = useState([]);
  // v17 — extra structured fields
  const [complianceChecks, setComplianceChecks] = useState({}); // {key: bool}
  const [widgetValues, setWidgetValues] = useState({});         // {widget_id: int|bool}
  const [behaviorIncidents, setBehaviorIncidents] = useState({}); // {trigger_id: {label, antecedent, behavior_details, de_escalation, notification_status}}
  const [escalationModal, setEscalationModal] = useState(null);   // {trigger_id, label, ...draft}
  const [submitting, setSubmitting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [voiceFp, setVoiceFp] = useState(null);
  // Catch-up mode: when the calendar pre-selects a date via ?date=YYYY-MM-DD, lock it.
  const catchUpDate = search.get("date");
  const isCatchUp = !!catchUpDate;
  // Admin Proxy mode: admin submits on behalf of a provider; signatures are HARD-LOCKED.
  const isAdminProxy = user?.role === "admin" && search.get("proxy") === "1";
  const proxyProviderId = search.get("provider_id") || "";
  const [proxyProviderName, setProxyProviderName] = useState("");

  const aiAssist = async () => {
    if (!activeClient) return toast.error("Select a client first");
    setAiBusy(true);
    try {
      // Build labeled custom answers so the LLM knows the semantic intent of each field
      const labeledCustom = (config.custom_questions || [])
        .map((q) => {
          const raw = customAnswers[q.id];
          // Skip empty answers so they don't crowd the prompt
          if (q.type === "checkbox") {
            return { label: q.label, type: "checkbox", value: !!raw };
          }
          if (q.type === "select") {
            return raw ? { label: q.label, type: "select", value: raw } : null;
          }
          // text / textarea
          return (raw && String(raw).trim()) ? { label: q.label, type: q.type || "text", value: raw } : null;
        })
        .filter(Boolean);
      const { data } = await api.post("/ai/chart-draft", {
        client_first_name: clientFirstName,
        attendance: form.attendance,
        overnight_stayed: form.overnight_stayed,
        activities: form.activities,
        mood: form.mood,
        meals: form.meals,
        attendance_checks: form.attendance_checks,
        goals: goals.map((g) => ({
          title: g.title,
          worked_on: !!goalProgress[g.id]?.worked_on,
          progress: goalProgress[g.id]?.progress || "",
          status: goalProgress[g.id]?.status || "",
        })),
        custom_answers_labeled: labeledCustom,
        compliance_checks: complianceChecks,
        behavior_incidents: Object.values(behaviorIncidents),
        widget_values: widgetValues,
        widget_definitions: activeClient?.interactive_widgets || [],
        // Voice Fingerprint: when admin uses Proxy Entry, sample the proxied contractor's
        // past notes (not the admin's) so the draft matches THEIR writing voice.
        on_behalf_of_provider_id: isAdminProxy ? (proxyProviderId || null) : null,
      });
      setForm((f) => ({ ...f, summary_note: data.draft }));
      setVoiceFp(data.voice_fingerprint || null);
      if (data.voice_fingerprint?.active) {
        toast.success(`AI summary generated — matched to your voice (${data.voice_fingerprint.samples_used} sample${data.voice_fingerprint.samples_used === 1 ? "" : "s"})`);
      } else {
        toast.success("AI summary generated — edit before signing");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI Summary failed");
    } finally { setAiBusy(false); }
  };

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      const fromUrl = search.get("client");
      const saved = localStorage.getItem("oppo_active_client");
      setClientId(data.find((c) => c.id === fromUrl)?.id || data.find((c) => c.id === saved)?.id || data[0]?.id || "");
    });
    api.get("/form-configs/daily_note").then(({ data }) => {
      const f = data?.fields || {};
      if (!f.sections) f.sections = [];
      if (!f.custom_questions) f.custom_questions = [];
      setConfig(f);
    });
    if (isAdminProxy && proxyProviderId) {
      api.get(`/users/providers/${proxyProviderId}`).then(({ data }) => {
        setProxyProviderName(data?.name || "selected provider");
      }).catch(() => setProxyProviderName("selected provider"));
    }
  }, []);

  useEffect(() => {
    if (!clientId) return;
    localStorage.setItem("oppo_active_client", clientId);
    api.get(`/clients/${clientId}/goals`).then(({ data }) => {
      // v35 — defensive de-dupe by goal id (and title fallback) so a corrupted
      // PCSP import that doubled-up the same goal can't render every Yes/No
      // tracking question twice in the daily note form.
      const seenIds = new Set();
      const seenTitles = new Set();
      const unique = [];
      for (const g of (data || [])) {
        const id = g?.id;
        const t = (g?.title || "").trim().toLowerCase();
        if (id && seenIds.has(id)) continue;
        if (t && seenTitles.has(t)) continue;
        if (id) seenIds.add(id);
        if (t) seenTitles.add(t);
        unique.push(g);
      }
      setGoals(unique);
      const gp = {};
      unique.forEach((g) => { gp[g.id] = { worked_on: false, progress: "", status: "", support_items_checked: [], support_provided: {} }; });
      setGoalProgress(gp);
    });
    api.get(`/medications?client_id=${clientId}`).then(({ data }) => {
      // v37 — Defense-in-depth: even though the backend already filters
      // discontinued meds, strip any leftover rows from cached / legacy
      // payloads so the eMAR grid never surfaces a med that's been flagged
      // for discontinuation by any signal (status, discontinued_at, deleted).
      const onlyActive = (Array.isArray(data) ? data : []).filter((m) => (
        m && m.active !== false
        && m.status !== "discontinued"
        && !m.discontinued_at
        && m.deleted !== true
      ));
      setAllMeds(onlyActive);
      const next = {};
      onlyActive.filter((m) => !m.is_prn).forEach((m) => {
        next[m.id] = { am_status: "none", pm_status: "none", missed_reason: "" };
      });
      setEmarEntries(next);
      setPrnEntries([]);
    });
    // Reset v17 per-client structured fields when switching clients
    setComplianceChecks({});
    setWidgetValues({});
    setBehaviorIncidents({});
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/calendar-status?client_id=${clientId}&year=${calY}&month=${calM}`).then(({ data }) => setCal(data));
  }, [clientId, calY, calM]);

  const activeClient = clients.find((c) => c.id === clientId);
  const clientFirstName = activeClient?.name?.split(/\s+/)?.[0] || "Client";
  const routineMeds = useMemo(() => allMeds.filter((m) => !m.is_prn), [allMeds]);
  const prnMeds = useMemo(() => allMeds.filter((m) => m.is_prn), [allMeds]);

  const sectionData = (id) => config.sections?.find((s) => s.id === id);
  const visibleSections = (config.sections || []);

  const setSectionValue = (sectionId, value) => {
    if (sectionId === "activities") setForm((f) => ({ ...f, activities: value }));
    else if (sectionId === "mood") setForm((f) => ({ ...f, mood: value }));
    else if (sectionId === "meals") setForm((f) => ({ ...f, meals: value }));
    else if (sectionId === "attendance_checks") setForm((f) => ({ ...f, attendance_checks: value }));
    else setCustomAnswers((c) => ({ ...c, [sectionId]: value }));
  };
  const getSectionValue = (sectionId) => {
    if (sectionId === "activities") return form.activities;
    if (sectionId === "mood") return form.mood;
    if (sectionId === "meals") return form.meals;
    if (sectionId === "attendance_checks") return form.attendance_checks;
    return customAnswers[sectionId] || [];
  };

  const cycleStatus = (medId, slot) => {
    setEmarEntries((e) => {
      const cur = e[medId]?.[slot] || "none";
      const ni = (STATUSES.indexOf(cur) + 1) % STATUSES.length;
      return { ...e, [medId]: { ...e[medId], [slot]: STATUSES[ni] } };
    });
  };
  const setMissedReason = (medId, reason) => setEmarEntries((e) => ({ ...e, [medId]: { ...e[medId], missed_reason: reason } }));
  const selectAll = (slot, status) => {
    const n = { ...emarEntries };
    routineMeds.forEach((m) => { n[m.id] = { ...n[m.id], [slot]: status }; });
    setEmarEntries(n);
  };

  const addPrn = () => setPrnEntries((arr) => [...arr, { id: crypto.randomUUID(), medication_id: "", name: "", dosage: "", time: "", reason: "" }]);
  const updatePrn = (id, patch) => setPrnEntries((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePrn = (id) => setPrnEntries((arr) => arr.filter((p) => p.id !== id));

  const flagMedStatus = async (med, status) => {
    const note = window.prompt(`Note for marking ${med.name} as ${status} (optional):`) || "";
    try {
      await api.post(`/medications/${med.id}/status-change`, { status, note });
      toast.success(`${med.name} flagged ${status}. Admin notified.`);
    } catch (e) { toast.error("Could not flag medication"); }
  };

  // ---- Medication change request dialog (action-aware) -------------------
  // Provider never mutates live `medications`; every change ladders through
  // db.med_requests and only goes live once an admin approves.
  const [medReqOpen, setMedReqOpen] = useState(false);
  const [medReqAction, setMedReqAction] = useState("new"); // new | discontinue | dose_change | frequency_change
  const [medReqTarget, setMedReqTarget] = useState(null);  // existing medication object for non-"new"
  const [medReqForm, setMedReqForm] = useState({
    name: "", dose: "", schedule: "AM/PM", is_prn: false,
    proposed_dose: "", proposed_schedule: "", reason: "",
  });
  const [medReqBusy, setMedReqBusy] = useState(false);

  const openMedRequest = (action, med = null) => {
    setMedReqAction(action);
    setMedReqTarget(med);
    setMedReqForm({
      name: med?.name || "",
      dose: med?.dose || "",
      schedule: med?.schedule || "AM/PM",
      is_prn: med?.is_prn || false,
      proposed_dose: med?.dose || "",
      proposed_schedule: med?.schedule || "AM/PM",
      reason: "",
    });
    setMedReqOpen(true);
  };

  const submitMedRequest = async () => {
    const f = medReqForm;
    if (medReqAction === "new" && (!f.name.trim() || !f.dose.trim())) {
      return toast.error("Medication name and dose are required");
    }
    if (medReqAction === "dose_change" && !f.proposed_dose.trim()) {
      return toast.error("Enter the proposed dose");
    }
    if (medReqAction === "frequency_change" && !f.proposed_schedule.trim()) {
      return toast.error("Enter the proposed schedule");
    }
    if (!f.reason.trim()) {
      return toast.error("Reason is required so admin can audit the change");
    }
    setMedReqBusy(true);
    try {
      const payload = {
        client_id: clientId,
        action: medReqAction,
        reason: f.reason,
      };
      if (medReqAction === "new") {
        Object.assign(payload, { name: f.name, dose: f.dose, schedule: f.schedule, is_prn: f.is_prn });
      } else {
        payload.medication_id = medReqTarget.id;
        if (medReqAction === "dose_change") payload.proposed_dose = f.proposed_dose;
        if (medReqAction === "frequency_change") payload.proposed_schedule = f.proposed_schedule;
      }
      await api.post("/med-requests", payload);
      toast.success("Request sent — admin will review before the live eMAR changes");
      setMedReqOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send request");
    } finally { setMedReqBusy(false); }
  };

  const requestNewMedication = () => openMedRequest("new", null);

  const submit = async () => {
    if (!clientId) return toast.error("Select a client");
    if (!isAdminProxy && !form.signature_data) return toast.error("Signature required");
    if (!form.overnight_stayed && !form.overnight_reason?.trim()) {
      return toast.error("When the client did not stay overnight, an overnight reason is required.");
    }
    const emarSkipped = !!activeClient?.emar_disabled;
    if (!emarSkipped) {
      for (const m of routineMeds) {
        const e = emarEntries[m.id] || {};
        if ((e.am_status === "missed" || e.pm_status === "missed") && !e.missed_reason?.trim()) {
          return toast.error(`Provide a reason for missed dose of ${m.name}`);
        }
      }
      for (const p of prnEntries) {
        if (!p.medication_id) return toast.error("Pick a PRN medication for each PRN entry");
        if (!p.reason?.trim()) return toast.error("PRN entries require a reason");
      }
    }
    setSubmitting(true);
    try {
      const onlyMedsPayload = !form.overnight_stayed;
      await api.post("/daily-notes-combined", {
        client_id: clientId,
        date: form.date,
        attendance: form.attendance,
        overnight_stayed: form.overnight_stayed,
        overnight_reason: form.overnight_reason || "",
        on_behalf_of_provider_id: isAdminProxy ? proxyProviderId : "",
        activities: onlyMedsPayload ? [] : form.activities,
        mood: onlyMedsPayload ? [] : form.mood,
        meals: onlyMedsPayload ? [] : form.meals,
        attendance_checks: onlyMedsPayload ? [] : form.attendance_checks,
        summary_note: onlyMedsPayload ? "" : form.summary_note,
        goals_worked_on: onlyMedsPayload ? [] : goals.map((g) => ({
          goal_id: g.id, title: g.title,
          worked_on: !!goalProgress[g.id]?.worked_on,
          progress: goalProgress[g.id]?.progress || "",
          status: goalProgress[g.id]?.status || "",
          support_items_checked: goalProgress[g.id]?.support_items_checked || [],
          support_provided: goalProgress[g.id]?.support_provided || {},
          flagged_for_review: !!goalProgress[g.id]?.flagged_for_review,
          flag_note: goalProgress[g.id]?.flag_note || "",
          suggested_replacement: goalProgress[g.id]?.suggested_replacement || "",
        })),
        custom_answers: onlyMedsPayload ? {} : customAnswers,
        compliance_checks: onlyMedsPayload ? {} : complianceChecks,
        behavior_incidents: onlyMedsPayload ? [] : Object.values(behaviorIncidents),
        widget_values: onlyMedsPayload ? {} : widgetValues,
        emar_entries: emarSkipped ? [] : routineMeds.map((m) => ({
          medication_id: m.id, name: m.name, dosage: m.dosage,
          am_status: medUsesSlot(m, "am") ? (emarEntries[m.id]?.am_status || "none") : "none",
          pm_status: medUsesSlot(m, "pm") ? (emarEntries[m.id]?.pm_status || "none") : "none",
          missed_reason: emarEntries[m.id]?.missed_reason || "",
          flagged_for_review: !!emarEntries[m.id]?.flagged_for_review,
          flag_note: emarEntries[m.id]?.flag_note || "",
        })),
        emar_prn_entries: emarSkipped ? [] : prnEntries.map((p) => {
          const med = prnMeds.find((m) => m.id === p.medication_id);
          return { medication_id: p.medication_id, name: med?.name || "", dosage: med?.dosage || "", time: p.time, reason: p.reason, followup_efficacy: p.followup_efficacy || "" };
        }),
        signature_data: form.signature_data,
        signature_type: form.signature_type,
      });
      toast.success("Daily Note + eMAR submitted");
      navigate("/provider");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  };

  // Build interactive calendar for date selection
  const firstDow = cal ? new Date(calY, calM - 1, 1).getDay() : 0;
  const cells = [];
  if (cal) {
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= cal.total_days; d++) cells.push(d);
  }
  const noteSet = new Set(cal?.note_dates || []);
  const emarSet = new Set(cal?.emar_dates || []);
  const hasMeds = cal?.has_medications;
  const todayObj = new Date();

  return (
    <div className="space-y-5" data-testid="daily-note-form">
      {isAdminProxy && (
        <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2" data-testid="admin-proxy-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Admin Proxy Entry — Read-only attestation</p>
            <p className="text-amber-800 text-xs leading-relaxed mt-0.5">
              You are completing this form on behalf of <span className="font-semibold">{proxyProviderName || "the assigned contractor"}</span>. 
              Signature canvases and 'I Attest' checkboxes are disabled. Every submission saves an
              audit stamp tying the entry back to your admin account.
            </p>
          </div>
        </div>
      )}
      <div>
        <p className="text-label">Form</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">Daily Service Note</h2>
        <p className="text-sm text-muted-foreground">Daily Note + eMAR in one submission.</p>
      </div>

      {isCatchUp && (
        <div
          className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
          data-testid="catch-up-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-semibold text-amber-900">
              Catch-up mode — locked to {form.date}
            </p>
            <p className="text-amber-800 text-xs leading-relaxed mt-0.5">
              You opened this form from the dashboard to back-fill a missed day.
              Date and client are locked.
              {" "}
              <button
                type="button"
                onClick={() => navigate("/provider/daily-note")}
                className="underline font-semibold hover:text-amber-900"
                data-testid="catch-up-exit"
              >
                Switch to today
              </button>
              {" or "}
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="underline font-semibold hover:text-amber-900"
                data-testid="catch-up-back"
              >
                go back
              </button>
              .
            </p>
          </div>
        </div>
      )}

      <ClientSwitcher clients={clients} activeId={clientId} onChange={setClientId} disabled={isCatchUp} />

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-label">Date of Service</p>
          {isCatchUp && (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 rounded-md font-semibold" data-testid="catch-up-badge">
              <AlertTriangle className="w-3 h-3" /> Catch-up mode — date locked
            </span>
          )}
        </div>
        {!isCatchUp && (
          <div className="flex justify-center">
            <MonthNav year={calY} month={calM} onChange={(y, m) => { setCalY(y); setCalM(m); }} testId="date-picker-month-nav" />
          </div>
        )}
        {cal && !isCatchUp && (
          <>
            <div className="grid grid-cols-7 gap-1">
              {["S","M","T","W","T","F","S"].map((d, i) => (
                <p key={`dow-${i}`} className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground text-center py-0.5">{d}</p>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="date-picker-grid">
              {cells.map((d, i) => {
                if (d == null) return <div key={`empty-${i}`} />;
                const dateStr = `${calY}-${pad(calM)}-${pad(d)}`;
                const hasNote = noteSet.has(dateStr);
                const hasEmar = emarSet.has(dateStr);
                const submitted = hasNote && (!hasMeds || hasEmar);
                const dateObj = new Date(calY, calM - 1, d);
                const isFuture = dateObj > todayObj;
                const selected = form.date === dateStr;
                const cls = submitted ? "bg-emerald-500 border-emerald-600 text-white"
                  : isFuture ? "bg-white border-border text-muted-foreground"
                  : "bg-red-500 border-red-600 text-white";
                return (
                  <button
                    type="button"
                    key={dateStr}
                    onClick={() => setForm((f) => ({ ...f, date: dateStr }))}
                    className={`aspect-square rounded-md border-2 text-sm font-semibold transition-transform hover:scale-105 ${cls} ${selected ? "ring-2 ring-primary ring-offset-1" : ""}`}
                    data-testid={`date-cell-${d}`}
                  >{d}</button>
                );
              })}
            </div>
          </>
        )}
        <p className="text-center text-sm">
          Selected: <span className="font-display font-bold text-primary" data-testid="selected-date">{form.date}</span>
        </p>
      </Card>

      <Card className="p-4 space-y-3">
        <div>
          <p className="text-label mb-2">Attendance</p>
          <div className="grid grid-cols-2 gap-2">
            {["present", "absent"].map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setForm({ ...form, attendance: a })}
                className={`tap-target p-3 rounded-md border-2 capitalize text-base ${
                  form.attendance === a ? "border-primary bg-primary/5 font-semibold" : "border-border bg-white"
                }`}
                data-testid={`attendance-${a}`}
              >{a}</button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-3 p-3 rounded-md border-2 border-border bg-white cursor-pointer" data-testid="overnight-block">
          <Checkbox
            checked={form.overnight_stayed}
            onCheckedChange={(v) => setForm((f) => ({ ...f, overnight_stayed: !!v, overnight_reason: !!v ? "" : f.overnight_reason }))}
            className="h-6 w-6"
            data-testid="overnight-checkbox"
          />
          <span className="text-base font-medium">
            Did {clientFirstName} stay overnight?
          </span>
        </label>

        {!form.overnight_stayed && (
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="overnight-no-block">
            <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Client Did Not Stay Overnight
            </p>
            <p className="text-xs text-amber-800">
              {activeClient?.emar_disabled
                ? "Narrative, behaviour, and daily checkbox sections will be skipped for this date."
                : "Narrative, behaviour, and daily checkbox sections will be skipped — only the eMAR record is required."}
              {" "}This date will display as <span className="font-mono bg-slate-900 text-white px-1.5 py-0.5 rounded">Black</span> on dashboards and excluded from billable days.
            </p>
            <div>
              <Label className="text-label text-amber-900">Documented reason (required)</Label>
              <Select value={form.overnight_reason} onValueChange={(v) => setForm((f) => ({ ...f, overnight_reason: v }))}>
                <SelectTrigger className="bg-white" data-testid="overnight-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {["Hospital Stay", "Family Visit", "Respite Setting", "Vacation / Trip", "Other"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>

      {form.overnight_stayed && goals.length > 0 && (
        <Card className="p-4 border-l-4 border-l-primary bg-primary/5" data-testid="pcsp-goals-block">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-primary" />
            <p className="text-label">PCSP Goals — Daily Status</p>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            For each goal, mark Yes or No on every Support Strategy line, then pick the overall status (Improved / Maintained / Regressed).
            Selecting <span className="font-semibold text-red-700">Regressed</span> opens a mandatory behavior-escalation form.
          </p>
          <div className="space-y-3">
            {goals.map((g) => {
              const supportLines = splitSupportLines(g.description, g.support_items, g.title);
              const provided = goalProgress[g.id]?.support_provided || {};
              const yesCount = supportLines.filter((l) => provided[l] === "yes").length;
              const noCount = supportLines.filter((l) => provided[l] === "no").length;
              return (
              <div key={g.id} className="border border-border rounded-md p-3 bg-white space-y-2" data-testid={`goal-${g.id}`}>
                <p className="font-display font-semibold text-sm">{g.title}</p>
                {supportLines.length > 0 && (
                  <div className="rounded-md bg-secondary/40 border border-border p-2" data-testid={`goal-supports-${g.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                        Support Strategies — Provided today?
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground" data-testid={`goal-supports-tally-${g.id}`}>
                        {yesCount} Yes · {noCount} No · {supportLines.length - yesCount - noCount} Pending
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {supportLines.map((line, idx) => {
                        const val = provided[line];
                        return (
                          <div
                            key={idx}
                            className="grid grid-cols-[1fr_auto] gap-2 items-center bg-white border border-border rounded p-2"
                            data-testid={`goal-support-row-${g.id}-${idx}`}
                          >
                            <span className="text-xs">{line}</span>
                            <div className="inline-flex rounded-md overflow-hidden border border-border" role="radiogroup" aria-label={`Provided support: ${line}`}>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={val === "yes"}
                                onClick={() => setGoalProgress((gp) => {
                                  const nextProvided = { ...(gp[g.id]?.support_provided || {}), [line]: "yes" };
                                  const checkedList = Object.keys(nextProvided).filter((k) => nextProvided[k] === "yes");
                                  return {
                                    ...gp,
                                    [g.id]: {
                                      ...gp[g.id],
                                      support_provided: nextProvided,
                                      support_items_checked: checkedList,   // legacy mirror
                                      worked_on: true,
                                    },
                                  };
                                })}
                                className={`px-3 py-1 text-[11px] font-semibold border-r border-border ${val === "yes" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 hover:bg-emerald-50"}`}
                                data-testid={`goal-support-yes-${g.id}-${idx}`}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={val === "no"}
                                onClick={() => setGoalProgress((gp) => {
                                  const nextProvided = { ...(gp[g.id]?.support_provided || {}), [line]: "no" };
                                  const checkedList = Object.keys(nextProvided).filter((k) => nextProvided[k] === "yes");
                                  return {
                                    ...gp,
                                    [g.id]: {
                                      ...gp[g.id],
                                      support_provided: nextProvided,
                                      support_items_checked: checkedList,
                                      worked_on: true,
                                    },
                                  };
                                })}
                                className={`px-3 py-1 text-[11px] font-semibold ${val === "no" ? "bg-red-600 text-white" : "bg-white text-red-700 hover:bg-red-50"}`}
                                data-testid={`goal-support-no-${g.id}-${idx}`}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground pt-1">
                  Overall daily status
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {GOAL_STATUSES.map((s) => {
                    const selected = goalProgress[g.id]?.status === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => {
                          setGoalProgress((gp) => ({
                            ...gp,
                            [g.id]: { ...gp[g.id], status: s.value, worked_on: true },
                          }));
                          if (s.value === "regressed") {
                            const triggerId = `goal-${g.id}`;
                            setEscalationModal({
                              trigger_id: triggerId,
                              label: `Goal regression — ${g.title}`,
                              antecedent: behaviorIncidents[triggerId]?.antecedent || "",
                              behavior_details: behaviorIncidents[triggerId]?.behavior_details || "",
                              de_escalation: behaviorIncidents[triggerId]?.de_escalation || "",
                              notification_status: behaviorIncidents[triggerId]?.notification_status || "",
                            });
                          } else {
                            // Switching away from Regressed — clear any captured escalation for this goal
                            setBehaviorIncidents((b) => {
                              const next = { ...b };
                              delete next[`goal-${g.id}`];
                              return next;
                            });
                          }
                        }}
                        className={`tap-target h-11 rounded-md border-2 text-sm font-semibold transition-colors ${
                          selected ? s.color : "border-border bg-white text-muted-foreground hover:bg-secondary"
                        }`}
                        data-testid={`goal-status-${g.id}-${s.value}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {behaviorIncidents[`goal-${g.id}`] && (
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3" /> Regression escalation captured ·{" "}
                    <button
                      type="button"
                      className="underline font-semibold"
                      onClick={() => setEscalationModal({
                        trigger_id: `goal-${g.id}`,
                        ...behaviorIncidents[`goal-${g.id}`],
                      })}
                      data-testid={`edit-regression-${g.id}`}
                    >Review</button>
                  </p>
                )}
                <Input
                  placeholder="Progress notes (optional)"
                  value={goalProgress[g.id]?.progress || ""}
                  onChange={(e) => setGoalProgress((gp) => ({ ...gp, [g.id]: { ...gp[g.id], progress: e.target.value } }))}
                  className="h-10 text-sm" data-testid={`goal-progress-${g.id}`}
                />
                {/* v26 — flag-for-review toggle bubbles up to admin alert badge */}
                <div className="flex items-start gap-2 pt-1 border-t border-dashed border-border">
                  <Checkbox
                    checked={!!goalProgress[g.id]?.flagged_for_review}
                    onCheckedChange={(v) => setGoalProgress((gp) => ({ ...gp, [g.id]: { ...gp[g.id], flagged_for_review: !!v } }))}
                    data-testid={`goal-flag-${g.id}`}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label className="text-[11px] font-semibold text-amber-700 cursor-pointer flex items-center gap-1">
                      <Flag className="w-3 h-3" /> Flag for admin review
                    </label>
                  {goalProgress[g.id]?.flagged_for_review && (
                    <div className="space-y-1.5 mt-1.5">
                      <Input
                        placeholder="Why does this question/goal need admin attention?"
                        value={goalProgress[g.id]?.flag_note || ""}
                        onChange={(e) => setGoalProgress((gp) => ({ ...gp, [g.id]: { ...gp[g.id], flag_note: e.target.value } }))}
                        className="h-8 text-xs bg-amber-50/50 border-amber-200" data-testid={`goal-flag-note-${g.id}`}
                      />
                      <Input
                        placeholder="Suggested replacement or improvement (optional)"
                        value={goalProgress[g.id]?.suggested_replacement || ""}
                        onChange={(e) => setGoalProgress((gp) => ({ ...gp, [g.id]: { ...gp[g.id], suggested_replacement: e.target.value } }))}
                        className="h-8 text-xs bg-amber-50/50 border-amber-200" data-testid={`goal-flag-replacement-${g.id}`}
                      />
                    </div>
                  )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </Card>
      )}

      {form.overnight_stayed && (activeClient?.behavior_triggers || []).length > 0 && (
        <Card className="p-4 border-l-4 border-l-red-600 bg-red-50/40" data-testid="behavior-triggers-block">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-red-600" />
            <p className="text-label text-red-900">Client-Specific Behavior &amp; Risk Tracking</p>
          </div>
          <p className="text-xs text-red-800 mb-3">
            Toggling any item ON opens a mandatory escalation form (antecedent, behavior, de-escalation, notification).
          </p>
          <div className="space-y-2">
            {(activeClient?.behavior_triggers || []).map((t) => {
              const isTrue = !!behaviorIncidents[t.id];
              return (
                <div key={t.id} className="space-y-1" data-testid={`behavior-row-${t.id}`}>
                  <label
                    className={`flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer ${isTrue ? "border-red-500 bg-red-50" : "border-border bg-white hover:bg-secondary/40"}`}
                  >
                    <Checkbox
                      checked={isTrue}
                      onCheckedChange={(v) => {
                        if (v) {
                          setEscalationModal({
                            trigger_id: t.id,
                            label: t.label,
                            antecedent: "",
                            behavior_details: "",
                            de_escalation: "",
                            notification_status: "",
                          });
                        } else {
                          setBehaviorIncidents((b) => {
                            const next = { ...b };
                            delete next[t.id];
                            return next;
                          });
                        }
                      }}
                      className="h-6 w-6"
                      data-testid={`behavior-${t.id}`}
                    />
                    <span className="text-sm flex-1 font-medium">{t.label}</span>
                    {isTrue && (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-700 underline"
                        onClick={() => setEscalationModal({ trigger_id: t.id, ...behaviorIncidents[t.id] })}
                        data-testid={`behavior-review-${t.id}`}
                      >Review</button>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {form.overnight_stayed && (activeClient?.interactive_widgets || []).length > 0 && (
        <Card className="p-4 border-l-4 border-l-sky-500 bg-sky-50/40" data-testid="widgets-block">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-sky-600" />
            <p className="text-label text-sky-900">Client-Specific Tracking</p>
          </div>
          <div className="space-y-3">
            {(activeClient?.interactive_widgets || []).map((w) => {
              if (w.type === "counter") {
                const value = Number(widgetValues[w.id] ?? 0);
                return (
                  <div key={w.id} className="rounded-md border border-sky-200 bg-white p-3" data-testid={`widget-counter-${w.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-sky-600" />
                        <p className="font-display font-semibold text-sm">{w.label}</p>
                      </div>
                      {w.target && <span className="text-[10px] uppercase tracking-wide bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full font-semibold">Target: {w.target}</span>}
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-4">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setWidgetValues((v) => ({ ...v, [w.id]: Math.max(0, Number(v[w.id] ?? 0) - 1) }))}
                        data-testid={`widget-counter-dec-${w.id}`}
                        className="h-12 w-12 p-0"
                      ><Minus className="w-5 h-5" /></Button>
                      <span className="font-display text-3xl font-bold text-sky-700 min-w-[3rem] text-center" data-testid={`widget-counter-value-${w.id}`}>{value}</span>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setWidgetValues((v) => ({ ...v, [w.id]: Number(v[w.id] ?? 0) + 1 }))}
                        data-testid={`widget-counter-inc-${w.id}`}
                        className="h-12 w-12 p-0"
                      ><Plus className="w-5 h-5" /></Button>
                    </div>
                  </div>
                );
              }
              if (w.type === "checkbox") {
                const checked = !!widgetValues[w.id];
                return (
                  <label
                    key={w.id}
                    className={`flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer ${checked ? "border-emerald-500 bg-emerald-50" : "border-border bg-white hover:bg-secondary/40"}`}
                    data-testid={`widget-checkbox-${w.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => setWidgetValues((vs) => ({ ...vs, [w.id]: !!v }))}
                      className="h-6 w-6 mt-0.5"
                      data-testid={`widget-checkbox-input-${w.id}`}
                    />
                    <div className="flex-1">
                      <p className="font-display font-semibold text-sm">{w.label}</p>
                      {w.target && <p className="text-xs text-muted-foreground mt-0.5">{w.target}</p>}
                    </div>
                  </label>
                );
              }
              return null;
            })}
          </div>
        </Card>
      )}

      {/* Behavior escalation modal — mandatory documentation when a trigger fires */}
      <Dialog open={!!escalationModal} onOpenChange={(v) => { if (!v) setEscalationModal(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="escalation-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-700">
              <ShieldAlert className="w-5 h-5" /> Behavior Escalation Capture
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-foreground">{escalationModal?.label}</span> — document the
              antecedent, observed behavior, de-escalation strategy used, and notification status.
              All four fields are required.
            </DialogDescription>
          </DialogHeader>
          {escalationModal && (
            <div className="space-y-3">
              <div>
                <Label className="text-label">Antecedent (what happened just before)</Label>
                <Textarea
                  rows={2}
                  value={escalationModal.antecedent}
                  onChange={(e) => setEscalationModal((m) => ({ ...m, antecedent: e.target.value }))}
                  data-testid="escalation-antecedent"
                />
              </div>
              <div>
                <Label className="text-label">Behavior details (what you observed)</Label>
                <Textarea
                  rows={3}
                  value={escalationModal.behavior_details}
                  onChange={(e) => setEscalationModal((m) => ({ ...m, behavior_details: e.target.value }))}
                  data-testid="escalation-behavior"
                />
              </div>
              <div>
                <Label className="text-label">De-escalation strategy used</Label>
                <Textarea
                  rows={2}
                  value={escalationModal.de_escalation}
                  onChange={(e) => setEscalationModal((m) => ({ ...m, de_escalation: e.target.value }))}
                  data-testid="escalation-strategy"
                />
              </div>
              <div>
                <Label className="text-label">Notification status</Label>
                <Select
                  value={escalationModal.notification_status}
                  onValueChange={(v) => setEscalationModal((m) => ({ ...m, notification_status: v }))}
                >
                  <SelectTrigger data-testid="escalation-notification">
                    <SelectValue placeholder="Select notification status…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin_notified">Admin/Support Coordinator notified</SelectItem>
                    <SelectItem value="guardian_notified">Family/Guardian notified</SelectItem>
                    <SelectItem value="both_notified">Both Admin &amp; Guardian notified</SelectItem>
                    <SelectItem value="no_notification_yet">No notification sent yet — pending</SelectItem>
                    <SelectItem value="no_notification_needed">No notification needed (de-escalated)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Cancel — clear the trigger entirely (un-check it)
                if (escalationModal?.trigger_id) {
                  if (escalationModal.trigger_id.startsWith("goal-")) {
                    const gid = escalationModal.trigger_id.slice("goal-".length);
                    setGoalProgress((gp) => ({ ...gp, [gid]: { ...gp[gid], status: "" } }));
                  }
                  setBehaviorIncidents((b) => {
                    const next = { ...b };
                    delete next[escalationModal.trigger_id];
                    return next;
                  });
                }
                setEscalationModal(null);
              }}
              data-testid="escalation-cancel"
            >Cancel</Button>
            <Button
              onClick={() => {
                const m = escalationModal;
                if (!m) return;
                if (!m.antecedent.trim() || !m.behavior_details.trim() || !m.de_escalation.trim() || !m.notification_status) {
                  return toast.error("All four fields are required to capture this escalation.");
                }
                setBehaviorIncidents((b) => ({
                  ...b,
                  [m.trigger_id]: {
                    label: m.label,
                    antecedent: m.antecedent.trim(),
                    behavior_details: m.behavior_details.trim(),
                    de_escalation: m.de_escalation.trim(),
                    notification_status: m.notification_status,
                  },
                }));
                setEscalationModal(null);
                toast.success("Escalation captured");
              }}
              data-testid="escalation-save"
              className="bg-red-600 hover:bg-red-700 text-white"
            >Save escalation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {form.overnight_stayed && visibleSections.map((s) => (
        <Card key={s.id} className="p-4">
          <CheckGroup
            label={s.title}
            options={s.options || []}
            value={getSectionValue(s.id)}
            onChange={(v) => setSectionValue(s.id, v)}
            testIdPrefix={`sec-${s.id}`}
          />
        </Card>
      ))}

      {form.overnight_stayed && (config.custom_questions || []).map((q) => (
        <Card key={q.id} className="p-4" data-testid={`custom-q-${q.id}`}>
          <Label className="text-label">{q.label}</Label>
          {q.type === "checkbox" && (
            <label className="flex items-center gap-3 mt-2 cursor-pointer p-2">
              <Checkbox
                checked={!!customAnswers[q.id]}
                onCheckedChange={(v) => setCustomAnswers((c) => ({ ...c, [q.id]: !!v }))}
                className="h-6 w-6"
              />
              <span className="text-base">Yes</span>
            </label>
          )}
          {(q.type === "text" || q.type === "textarea") && (
            <Textarea
              rows={q.type === "textarea" ? 4 : 2}
              value={customAnswers[q.id] || ""}
              onChange={(e) => setCustomAnswers((c) => ({ ...c, [q.id]: e.target.value }))}
              className="mt-1"
            />
          )}
          {q.type === "select" && (
            <Select value={customAnswers[q.id] || ""} onValueChange={(v) => setCustomAnswers((c) => ({ ...c, [q.id]: v }))}>
              <SelectTrigger className="mt-1 h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </Card>
      ))}

      {form.overnight_stayed && (
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Label htmlFor="summary" className="text-label">Daily Note Narrative</Label>
              {voiceFp?.active && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold uppercase tracking-wide"
                  title={`Trained on ${voiceFp.samples_used} of your ${voiceFp.total_signed_notes} past signed notes`}
                  data-testid="voice-fingerprint-badge"
                >
                  <Sparkles className="w-3 h-3" /> Your voice
                </span>
              )}
            </div>
            <Button
              type="button" size="sm"
              onClick={aiAssist}
              disabled={aiBusy}
              className="bg-brand-navy hover:bg-brand-navy-deep text-white"
              data-testid="ai-summary-btn"
            >
              {aiBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {aiBusy ? "Generating…" : "Generate AI Summary"}
            </Button>
          </div>
          <p className="audit-rule mb-3" data-testid="audit-rule">
            Audit Rule: Do not just list what the client did. State what prompting or support you actively provided today and how the client responded. The AI summary uses all your checkbox / radio / text answers — edit freely before signing.
            {voiceFp?.active && (
              <span className="block mt-1 text-emerald-700 not-italic">
                Voice Fingerprint active — the draft mirrors the cadence and phrasing of your last {voiceFp.samples_used} signed note{voiceFp.samples_used === 1 ? "" : "s"}.
              </span>
            )}
          </p>
          <Textarea
            id="summary" rows={6} value={form.summary_note}
            onChange={(e) => setForm({ ...form, summary_note: e.target.value })}
            placeholder="Click Generate AI Summary to draft a narrative from your form selections, or type your own."
            className="mt-1" data-testid="summary-note"
          />
        </Card>
      )}

      {/* Integrated eMAR sequence (hidden when admin disables it for this client) */}
      {!activeClient?.emar_disabled && (
      <Card className="p-4 space-y-3" data-testid="emar-integrated">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Pill className="w-4 h-4 text-primary" />
            <p className="text-label">eMAR (mandatory)</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={requestNewMedication} data-testid="request-new-med">
            <Plus className="w-4 h-4 mr-1" /> Propose new medication
          </Button>
        </div>

        {routineMeds.length === 0 && prnMeds.length === 0 && (
          <p className="text-sm text-muted-foreground bg-secondary/40 rounded-md p-3 text-center">
            This client has no assigned medications. eMAR auto-skipped.
          </p>
        )}

        {routineMeds.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => selectAll("am_status", "administered")} data-testid="select-all-am">Select All AM Given</Button>
              <Button type="button" variant="outline" className="h-11" onClick={() => selectAll("pm_status", "administered")} data-testid="select-all-pm">Select All PM Given</Button>
            </div>
            <div className="space-y-3">
              {routineMeds.map((m) => {
                const e = emarEntries[m.id] || {};
                const needsReason = e.am_status === "missed" || e.pm_status === "missed";
                return (
                  <div key={m.id} className="space-y-2 border-b border-border pb-3 last:border-0">
                    <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_120px_120px] gap-2 items-center">
                      <div className="min-w-0">
                        <p className="font-display font-semibold text-sm leading-tight">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.dose || m.dosage} · {m.schedule}</p>
                      </div>
                      <div className="w-[110px] sm:w-auto">
                        {medUsesSlot(m, "am") ? (
                          <StatusButton value={e.am_status || "none"} onClick={() => cycleStatus(m.id, "am_status")} slot="am" medId={m.id} />
                        ) : (
                          <div className="text-[10px] text-muted-foreground/50 text-center italic select-none" data-testid={`emar-slot-na-am-${m.id}`}>—</div>
                        )}
                      </div>
                      <div className="w-[110px] sm:w-auto">
                        {medUsesSlot(m, "pm") ? (
                          <StatusButton value={e.pm_status || "none"} onClick={() => cycleStatus(m.id, "pm_status")} slot="pm" medId={m.id} />
                        ) : (
                          <div className="text-[10px] text-muted-foreground/50 text-center italic select-none" data-testid={`emar-slot-na-pm-${m.id}`}>—</div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => openMedRequest("dose_change", m)} data-testid={`request-dose-${m.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> Request dose change
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => openMedRequest("frequency_change", m)} data-testid={`request-freq-${m.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> Request frequency change
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-xs h-8 text-red-600" onClick={() => openMedRequest("discontinue", m)} data-testid={`request-disc-${m.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> Request discontinue
                      </Button>
                    </div>
                    {needsReason && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-md p-3" data-testid={`missed-reason-block-${m.id}`}>
                        <Label className="text-label text-red-700 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5" /> Why was it missed? (required)
                        </Label>
                        <Textarea
                          value={e.missed_reason || ""} rows={2}
                          onChange={(ev) => setMissedReason(m.id, ev.target.value)}
                          placeholder="Client refused, hospital visit, vomiting..."
                          className="mt-1 bg-white" data-testid={`missed-reason-${m.id}`}
                        />
                      </div>
                    )}
                    {/* v26 — flag-for-review toggle (admin alert badge) */}
                    <div className="flex items-start gap-2 pt-1 border-t border-dashed border-border">
                      <Checkbox
                        checked={!!e.flagged_for_review}
                        onCheckedChange={(v) => setEmarEntries((prev) => ({ ...prev, [m.id]: { ...prev[m.id], flagged_for_review: !!v } }))}
                        data-testid={`emar-flag-${m.id}`}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label className="text-[11px] font-semibold text-amber-700 cursor-pointer flex items-center gap-1">
                          <Flag className="w-3 h-3" /> Flag for admin review
                        </label>
                        {e.flagged_for_review && (
                          <Input
                            placeholder="What does the admin need to look at on this med?"
                            value={e.flag_note || ""}
                            onChange={(ev) => setEmarEntries((prev) => ({ ...prev, [m.id]: { ...prev[m.id], flag_note: ev.target.value } }))}
                            className="h-9 text-xs mt-1" data-testid={`emar-flag-note-${m.id}`}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {prnMeds.length > 0 && (
          <div className="pt-3 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <p className="text-label">PRN (As-Needed) Administered Today</p>
              <Button type="button" variant="outline" size="sm" onClick={addPrn} data-testid="add-prn"><Plus className="w-4 h-4 mr-1" /> Log PRN</Button>
            </div>
            {prnEntries.length === 0 && <p className="text-xs text-muted-foreground">Tap "Log PRN" if you gave a PRN today.</p>}
            {prnEntries.map((p) => (
              <div key={p.id} className="border-2 border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-2 mb-2">
                <div className="flex items-center gap-2">
                  <Select value={p.medication_id} onValueChange={(v) => updatePrn(p.id, { medication_id: v })}>
                    <SelectTrigger className="flex-1" data-testid={`prn-med-${p.id}`}><SelectValue placeholder="PRN medication" /></SelectTrigger>
                    <SelectContent>{prnMeds.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.dose || m.dosage}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => removePrn(p.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
                <Input type="time" value={p.time} onChange={(e) => updatePrn(p.id, { time: e.target.value })} className="bg-white" />
                <Textarea value={p.reason} rows={2} onChange={(e) => updatePrn(p.id, { reason: e.target.value })} placeholder="Reason (e.g., headache)" className="bg-white" data-testid={`prn-reason-${p.id}`} />
                {/* v20 — PRN 30-minute follow-up compliance prompt */}
                <div className="rounded-md border border-amber-300 bg-amber-100/60 px-3 py-2 flex items-start gap-2" data-testid={`prn-followup-prompt-${p.id}`}>
                  <Clock className="w-4 h-4 text-amber-800 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-amber-900">Follow-up required in 30 minutes</p>
                    <p className="text-xs text-amber-900 mt-0.5">Log response efficacy below after re-checking the client at the 30-minute mark.</p>
                    <Textarea
                      value={p.followup_efficacy || ""}
                      onChange={(e) => updatePrn(p.id, { followup_efficacy: e.target.value })}
                      rows={2}
                      placeholder="e.g., Reported headache had subsided; client alert and engaged again."
                      className="bg-white mt-2"
                      data-testid={`prn-followup-${p.id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      <Card className="p-4 space-y-3">
        <p className="text-label">Electronic Signature *</p>
        {isAdminProxy ? (
          <div
            className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm"
            data-testid="proxy-signature-lock"
          >
            <p className="font-semibold text-amber-900 mb-1">Signature canvas hard-locked.</p>
            <p className="text-amber-800 text-xs leading-relaxed">
              Admins are physically restricted from executing legal signatures or attestations
              assigned to external contractors. This submission will be saved with an audit stamp:
              <span className="block mt-1 font-mono text-[11px]">
                Submitted via Admin Proxy by {user?.name} on behalf of {proxyProviderName} on {new Date().toLocaleString()}
              </span>
            </p>
          </div>
        ) : (
          <SignaturePad
            signerName={user?.name}
            onChange={({ signature_type, signature_data }) =>
              setForm((f) => ({ ...f, signature_type, signature_data }))
            }
          />
        )}
      </Card>

      <div className="sticky bottom-20 z-20">
        <Button onClick={submit} className="w-full h-14 text-base font-semibold shadow-lg" disabled={submitting} data-testid="submit-daily-note">
          {submitting ? "Submitting..." : "Submit Daily Note + eMAR"}
        </Button>
      </div>

      {/* Medication change request dialog (provider-initiated, admin-approved) */}
      <Dialog open={medReqOpen} onOpenChange={setMedReqOpen}>
        <DialogContent data-testid="med-request-dialog">
          <DialogHeader>
            <DialogTitle>
              {medReqAction === "new" && "Propose new medication"}
              {medReqAction === "discontinue" && `Request discontinue — ${medReqTarget?.name || ""}`}
              {medReqAction === "dose_change" && `Request dose change — ${medReqTarget?.name || ""}`}
              {medReqAction === "frequency_change" && `Request frequency change — ${medReqTarget?.name || ""}`}
            </DialogTitle>
            <DialogDescription>
              This goes to the admin queue. The live eMAR regimen only changes once an admin approves.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {medReqAction === "new" && (
              <>
                <div>
                  <Label className="text-label">Medication name *</Label>
                  <Input value={medReqForm.name} onChange={(e) => setMedReqForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g., Sertraline" data-testid="med-req-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-label">Dose *</Label>
                    <Input value={medReqForm.dose} onChange={(e) => setMedReqForm((f) => ({ ...f, dose: e.target.value }))} placeholder="50 mg" data-testid="med-req-dose" />
                  </div>
                  <div>
                    <Label className="text-label">Schedule</Label>
                    <Select value={medReqForm.schedule} onValueChange={(v) => setMedReqForm((f) => ({ ...f, schedule: v, is_prn: v === "PRN (As Needed)" }))}>
                      <SelectTrigger data-testid="med-req-schedule"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["AM Only", "PM Only", "Both (AM & PM)", "PRN (As Needed)"].map((f) => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={medReqForm.is_prn} onCheckedChange={(v) => setMedReqForm((f) => ({ ...f, is_prn: !!v }))} data-testid="med-req-prn" />
                  PRN (as-needed)
                </label>
              </>
            )}

            {medReqAction === "discontinue" && medReqTarget && (
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm">
                <p><strong>{medReqTarget.name}</strong> · {medReqTarget.dose || medReqTarget.dosage} · {medReqTarget.schedule}</p>
                <p className="text-xs text-rose-700 mt-1">On approval this medication will be marked inactive and removed from future eMAR cycles.</p>
              </div>
            )}

            {medReqAction === "dose_change" && (
              <div>
                <Label className="text-label">Proposed dose *</Label>
                <Input value={medReqForm.proposed_dose} onChange={(e) => setMedReqForm((f) => ({ ...f, proposed_dose: e.target.value }))} placeholder="e.g., 100 mg" data-testid="med-req-proposed-dose" />
                <p className="text-xs text-muted-foreground mt-1">Current dose: <span className="font-mono">{medReqTarget?.dose || medReqTarget?.dosage}</span></p>
              </div>
            )}

            {medReqAction === "frequency_change" && (
              <div>
                <Label className="text-label">Proposed schedule *</Label>
                <Select value={medReqForm.proposed_schedule} onValueChange={(v) => setMedReqForm((f) => ({ ...f, proposed_schedule: v, is_prn: v === "PRN (As Needed)" }))}>
                  <SelectTrigger data-testid="med-req-proposed-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["AM Only", "PM Only", "Both (AM & PM)", "PRN (As Needed)"].map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Current schedule: <span className="font-mono">{medReqTarget?.schedule}</span></p>
              </div>
            )}

            <div>
              <Label className="text-label">Reason / clinical justification *</Label>
              <Textarea
                rows={3}
                value={medReqForm.reason}
                onChange={(e) => setMedReqForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this change needed? (Pharmacy change, MD order, side effect…)"
                data-testid="med-req-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMedReqOpen(false)} disabled={medReqBusy} data-testid="med-req-cancel">Cancel</Button>
            <Button onClick={submitMedRequest} disabled={medReqBusy} data-testid="med-req-submit">
              {medReqBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Send to admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
